import type { Blocker } from 'react-router';

import { Button, Spinner } from '@shared';

import { useTranslations } from '@/i18n/client';

import type { UploadProgressSnapshot } from './upload-progress-context';

interface UploadProgressModalProps {
  progress: UploadProgressSnapshot;
  blocker?: Blocker;
}

function overallProgress(progress: UploadProgressSnapshot): number {
  if (progress.totalFiles <= 0) {
    return 0;
  }

  return Math.min(
    100,
    Math.round(
      ((progress.uploadedFiles + (progress.currentFileProgress ?? 0) / 100) / progress.totalFiles) * 100,
    ),
  );
}

export function UploadProgressModal({ progress, blocker }: UploadProgressModalProps) {
  const t = useTranslations('common.uploadProgress');
  const isPreparing = progress.totalFiles === 0;
  const percentage = overallProgress(progress);
  const navigationBlocked = blocker?.state === 'blocked';

  return (
    <div className="md-upload-overlay" role="presentation">
      <section
        className="md-upload-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="md-upload-progress-title"
        aria-describedby="md-upload-progress-description"
      >
        <div className="md-upload-modal__header">
          <span className="md-upload-modal__icon" aria-hidden="true">
            <Spinner label="" />
          </span>
          <div>
            <h2 id="md-upload-progress-title">{t('title')}</h2>
            <p id="md-upload-progress-description">{t('description')}</p>
          </div>
        </div>

        <div className="md-upload-modal__status" role="status" aria-live="polite">
          <strong>
            {isPreparing
              ? t('preparing')
              : t('status', { uploaded: progress.uploadedFiles, total: progress.totalFiles })}
          </strong>
          {progress.currentFileName ? (
            <span>{t('currentFile', { file: progress.currentFileName })}</span>
          ) : null}
        </div>

        <div className="md-upload-progress" aria-label={isPreparing ? t('preparing') : t('progressLabel')}>
          <div
            className={`md-upload-progress__bar${isPreparing ? ' md-upload-progress__bar--indeterminate' : ''}`}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={isPreparing ? undefined : percentage}
            aria-valuetext={isPreparing ? t('preparing') : `${percentage}%`}
            style={{ width: isPreparing ? '35%' : `${percentage}%` }}
          />
        </div>
        <div className="md-upload-progress__meta">
          {isPreparing ? (
            <span>{t('calculating')}</span>
          ) : (
            <>
              <span>{t('fileCount', { uploaded: progress.uploadedFiles, total: progress.totalFiles })}</span>
              <strong>{percentage} %</strong>
            </>
          )}
        </div>

        {navigationBlocked ? (
          <div className="md-upload-modal__warning" role="alert">
            <strong>{t('navigationTitle')}</strong>
            <p>{t('navigationWarning')}</p>
            <div className="md-upload-modal__actions">
              <Button type="button" variant="secondary" onClick={() => blocker?.reset()}>
                {t('stayAction')}
              </Button>
              <Button type="button" variant="danger" onClick={() => blocker?.proceed()}>
                {t('leaveAction')}
              </Button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
